import React, { useEffect, useState, useMemo } from 'react';
import useLadyService from '@/services/LadyService';
import { Button, Checkbox, Icon, Select } from '@/components/ui';
import { getDisabledFilters } from '@/stores/slices/filterSlice';
import { getIsMainPage } from '@/stores/slices/modelsState';
import { useDispatch, useSelector } from 'react-redux';
import FilterData from '@/components/filter/FilterData';
import { useFilterLogicContext } from '@/components/filter/FilterLogicProvider';
import { show, showShort } from '@/stores/slices/showFilterSlice';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
	getCountriesData,
	getCountrySlug,
	getCurrentLinkCity,
	getUserCurrentCity,
} from '@/stores/slices/userSlice';
import { getScrolledClass } from '@/stores/slices/headerSlice';
import { ScrollToTop } from '@/components/ui/ScrollToTop';
import { getLinksCitySlug } from '@/helper/linkCitySlug';

const Filters = () => {
	const { lang, windowWidth, t } = useLadyService();

	const disabled_filters = useSelector(getDisabledFilters);

	const dispatch = useDispatch();
	const currentCity = useSelector(getUserCurrentCity);
	const currentLinkCity = useSelector(getCurrentLinkCity);
	const countriesData = useSelector(getCountriesData);
	const selectedCountrySlug = useSelector(getCountrySlug);
	const isMainPage = useSelector(getIsMainPage);
	const filterData = FilterData();
	const params = useParams();
	const location = useLocation()

	const isJsdom =
		typeof window !== 'undefined' &&
		window.navigator.userAgent.includes('jsdom');
	const navigate = useNavigate();

	const FILTER = useFilterLogicContext();

	const options_place = useMemo(() => {
		const options = filterData.place;
		return {
			main_title: t('place'),
			options:
				windowWidth < 1400 && !isJsdom ? options.slice(0, 3) : options,
		};
	}, [lang, filterData, windowWidth]);

	const [selected_gender, setSelectedGender] = useState({
		id: 1000,
		title: t('pol'),
		value: 'all',
		icon: null,
	});

	const disabledSet = useMemo(() => {
		return new Set(disabled_filters);
	}, [disabled_filters]);

	const other_options = useMemo(() => {
		// const some_city = city !== 'czechia';
		return {
			gender: [
				{
					id: 1000,
					title: t('all'),
					value: 'all',
					icon: null,
				},

				{
					id: 67,
					title: windowWidth < 767 && !isJsdom ? ' ' : t('girls'),
					value: 'female',
					path: 'girls',
					// ...(some_city && {
					disabled: disabledSet.has('genderFemale'),
					// }),
					icon: 'gender-female',
				},

				{
					id: 70,
					title: windowWidth < 767 && !isJsdom ? ' ' : t('guys'),
					value: 'male',
					path: 'guys',
					// ...(some_city && {
					disabled: disabledSet.has('genderMale'),
					// }),
					icon: 'gender-male',
				},

				{
					id: 68,
					title: windowWidth < 767 && !isJsdom ? ' ' : t('trans'),
					value: 'trans',
					path: 'trans',
					// ...(some_city && {
					disabled: disabledSet.has('genderTrans'),
					// }),
					icon: 'gender-trans',
				},

				{
					id: 69,
					title: windowWidth < 767 && !isJsdom ? ' ' : t('couples'),
					value: 'couple',
					path: 'couple',
					// ...(some_city && {
					disabled: disabledSet.has('genderCouple'),
					// }),
					icon: 'gender-couple',
				},
			].sort((a, b) => {
				if (a.value === selected_gender) {
					return -1;
				}
				if (b.value === selected_gender) {
					return 1;
				}
				return 0;
			}),
		};
	}, [disabled_filters, lang, windowWidth]);

	const cityUrl = useMemo(() => {
		if (location.pathname.includes('/profile/') && !location.pathname.includes('/edit')) {
			return (
				(typeof currentLinkCity === 'string'
					? currentLinkCity
					: currentLinkCity?.slug) ??
				currentCity?.slug ??
				'czechia'
			);
		}

		return getLinksCitySlug({
			routeCitySlug: params.city,
			countriesData,
			currentCitySlug: currentCity?.slug,
			selectedCountrySlug,
			pathname: location.pathname,
			isMainPage,
		});
	}, [
		params.city,
		currentCity?.slug,
		currentLinkCity,
		location.pathname,
		countriesData,
		selectedCountrySlug,
		isMainPage,
	]);

	const setGender = async (value) => {
		const found = other_options.gender.find((i) => i.value === value);

		const obj = {
			value: value,
			path: found ? found.path : '-',
			title: found ? (value === 'all' ? t('pol') : found.title) : '-',
			icon: found ? found.icon : '-',
		};

		setSelectedGender(obj);

		if (value !== 'all') {
			if (!isMainPage) {
				console.log(`=> cityUrl: `, cityUrl);
				navigate(`/${lang}/${cityUrl}/${found.path}`);
			}
			FILTER.handleInstantRadio({ gender: [value] });
		} else {
			if (!isMainPage) {
				console.log(`=> cityUrl: `, cityUrl);
				navigate(`/${lang}/${cityUrl}`);
			}
			FILTER.handleInstantRadio({ gender: [] });
		}

		localStorage.setItem('save_gender_filter', value);
	};

	useEffect(() => {
		if (!isMainPage) {
			return;
		}

		const savedGender = localStorage.getItem('save_gender_filter');

		if (FILTER.filterState.gender.length === 1 && other_options.gender) {
			const transformed = FILTER.filterState.gender.map((i) =>
				FILTER.getFilterValue(i, true) === 'couple'
					? 'couple'
					: FILTER.getFilterValue(i, true),
			);

			const found = other_options.gender.find(
				(i) => i.value === transformed[0],
			);

			const obj = {
				value: transformed[0],
				title: found
					? transformed[0] === 'all'
						? t('pol')
						: found.title
					: '-',
				icon: found ? found.icon : '-',
			};

			setSelectedGender(obj);
		} else if (other_options.gender) {
			if (savedGender && !params.params) {
				const found = other_options.gender.find(
					(i) => i.value === savedGender && i.value !== 'all',
				);
				if (found) {
					setGender(found.value);
				} else {
					setSelectedGender({
						...other_options.gender[0],
						title: t('pol'),
					});
				}
			} else {
				setSelectedGender({
					...other_options.gender[0],
					title: t('pol'),
				});
			}
		}
	}, [FILTER.filterStateIndicator]);

	const handleOpenFilters = () => {
		dispatch(show());
	};

	const { resetFilter } = useFilterLogicContext();

	const scrolledClass = useSelector(getScrolledClass);

	const handleResetFilter = () => {
		resetFilter(true);

		if (scrolledClass) {
			ScrollToTop();
		}
	};

	return (
		<>
			{
				<form className={`header__filters ${isJsdom ? ' ssr' : ''}`}>
					{windowWidth > 1199.98 ||
					windowWidth < 767.98 ||
					isJsdom ? (
						<>
							{options_place.options.map((i) => {
								return !isJsdom ? (
									<Checkbox
										clazz={'header__bottom-filters'}
										key={i.id}
										name={i.name}
										id={i.id}
										value={i.value}
										title={i.title}
										checked={i.checked}
										buttonCheckbox={true}
										count={i.count}
										disabled={i.disabled}
										onChange={(e) => {
											if (!isMainPage) {
												navigate(
													`/${lang}/${cityUrl}/${e.target.value}`,
												);
											}

											FILTER.handleInstantCheckBox(e);
										}}
									/>
								) : i.disabled ? (
									<p
										className={`hover-line ${i.disabled ? ' _disabled' : ''}`}
									>
										{i.title}
									</p>
								) : (
									<a
										href={`/${lang}/${
											cityUrl
										}${i.value !== 'all' ? '/' + i.value : ''}`}
										disabled={i.disabled}
										className={`hover-line ${i.disabled ? ' _disabled' : ''}`}
									>
										{i.title}
									</a>
								);
							})}

							{/* genders selector */}
							{!isJsdom && (
								<Select
									name={'gender_select'}
									onChange={setGender}
									options={other_options.gender}
									arrowSize={'m'}
									withoutTitle={true}
									withIcon={true}
									defaulTitle={t('pol')}
									clazzWrapper={'select-field'}
									controlledValue={selected_gender.value}
									controlledTitle={selected_gender.title}
									controlledIcon={selected_gender.icon}
									clazzSvg={'color-green'}
								/>
							)}

							{isJsdom &&
								other_options.gender.map((i) => {
									return i.disabled ? (
										<p
											className={`hover-line m-0 ${i.disabled ? ' _disabled' : ''}`}
										>
											{i.title}
										</p>
									) : (
										<a
											href={
												lang === 'en' &&
												cityUrl === 'czechia' &&
												!i.path
													? '/'
													: `/${lang}/${
															cityUrl
														}${i.path ? '/' + i.path : ''}`
											}
											disabled={i.disabled}
											className={`hover-line ${i.disabled ? ' _disabled' : ''}`}
										>
											{i.title}
										</a>
									);
								})}

							{!isJsdom && (
								<div className="anker-wrapper">
									<Button
										id="navigation-filter-anker"
										onClick={handleOpenFilters}
										disabled={FILTER.showSkeletons}
										clazz={
											'header__filter button--primary' +
											' ' +
											(!FILTER.isFilterEmpty &&
											FILTER.isGenderAndServiceTypeOnly
												? 'checkbox-button_checked'
												: '')
										}
									>
										{t('filter')}
										<Icon
											size={'m'}
											spritePath={'filter'}
										/>
										{!FILTER.isFilterEmpty &&
											FILTER.isGenderAndServiceTypeOnly && (
												<span className="count"></span>
											)}
									</Button>
								</div>
							)}

							{!isJsdom && (
								<div className="reset_wrapper">
									{!FILTER.isFilterEmpty && (
										<Button
											onClick={handleResetFilter}
											size={'s'}
											title={t('reset')}
											clazz="header__reset hover"
										>
											{t('reset')}
										</Button>
									)}
								</div>
							)}
						</>
					) : null}

					{!isJsdom &&
						windowWidth < 1199.98 &&
						windowWidth > 767.98 && (
							<>
								<Button
									clazz={
										'button--secondary navigation__mobile-filter'
									}
									size={'xs'}
									onClick={() => dispatch(showShort())}
								>
									<Icon
										size={'m'}
										spritePath={'filter2'}
									/>
									{t('catalogue')}{' '}
									<Icon
										size={'s'}
										spritePath={'chevrone-down'}
									/>
								</Button>
								{!FILTER.isFilterEmpty && (
									<Button
										onClick={() => {
											FILTER.resetFilter(false);
										}}
										title={t('reset')}
										clazz="header__reset hover"
									>
										{t('reset')}
									</Button>
								)}
							</>
						)}
				</form>
			}
		</>
	);
};

export default Filters;
